/**
 * "Waiting on us" (home.html): the people the office owes a reply or a
 * finish, counted live from the screens that own them. Each item shows only
 * when the role may open that screen (and the office has the module); an
 * item that cannot load is left out rather than shown as zero. Items clear
 * themselves as the work gets done.
 */
import { Link } from '@tanstack/react-router';
import { QueueSchema as FormsQueueSchema } from '@velora/contracts/forms';
import { ThreadListResponseSchema } from '@velora/contracts/inbox';
import { MissedListResponseSchema } from '@velora/contracts/phones';
import { Skeleton } from '@velora/ui';
import { FileSignature, MessageSquare, NotebookPen, PhoneMissed } from 'lucide-react';
import type { ReactNode } from 'react';
import { useLive } from '../live/LiveProvider.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { useOfficeQuery } from '../office/useOfficeQuery.js';
import { useEntitlement, usePermission } from '../session/session.js';
import { notesApi, notesKeys } from '../../modules/notes/api.js';
import {
  defaultQueueProvider,
  localToday,
  notesTileLabel,
  queueRange,
} from '../../modules/notes/queueScope.js';

export interface WaitingItem {
  key: string;
  label: string;
  count: number;
  /** ISO time of the oldest one, when the source has it. */
  oldest: string | null;
  href: string;
  icon: ReactNode;
}

/** "2h 14m", "41m", "3d" since `iso`. */
export function ageLabel(iso: string, now = Date.now()): string {
  const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return min % 60 ? `${h}h ${min % 60}m` : `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function minIso(values: readonly (string | null | undefined)[]): string | null {
  let best: string | null = null;
  for (const v of values) if (v && (best === null || v < best)) best = v;
  return best;
}

/**
 * The "waiting on us" counts, each from the screen that owns it. Items the
 * role may not open, or whose source could not load, are left out (never a
 * made-up zero). Shared by the strip and Home's Needs attention list.
 */
export function useWaitingItems(
  /** Also count texts and missed calls (Home shows those as prompts instead). */
  { comms = true }: { comms?: boolean } = {},
): { items: WaitingItem[]; pending: boolean } {
  const canComms = usePermission('comms.view') && comms;
  const canPatients = usePermission('patient.view');
  const canNotes = usePermission('notes.view');
  const texting = useEntitlement('texting').active;
  useLive('inbox.thread');

  const texts = useOfficeQuery({
    queryKey: ['inbox.thread', 'home-waiting'],
    queryFn: (c, signal) =>
      c.request('/inbox/threads?view=actionable&split=patients&limit=200', {
        schema: ThreadListResponseSchema,
        signal,
      }),
    enabled: canComms && texting,
    refetchInterval: 60_000,
    retry: false,
  });
  const missed = useOfficeQuery({
    queryKey: ['call', 'home-waiting'],
    queryFn: (c, signal) =>
      c.request('/phones/missed', { schema: MissedListResponseSchema, signal }),
    enabled: canComms,
    refetchInterval: 60_000,
    retry: false,
  });
  const forms = useOfficeQuery({
    queryKey: ['form_packet', 'home-waiting'],
    queryFn: (c, signal) => c.request('/forms/queue', { schema: FormsQueueSchema, signal }),
    enabled: canPatients,
    refetchInterval: 2 * 60_000,
    retry: false,
  });
  // The same query as the Notes due page opens with (one definition of "to sign").
  const canSign = usePermission('notes.sign');
  const notesScope = defaultQueueProvider(canSign);
  const notesRange = queueRange(localToday());
  const notes = useOfficeQuery({
    queryKey: notesKeys.queue('unsigned', notesScope, notesRange.from, notesRange.to),
    queryFn: (c, signal) =>
      notesApi.queue(c, 'unsigned', notesScope, notesRange.from, notesRange.to, signal),
    enabled: canNotes,
    refetchInterval: 2 * 60_000,
    retry: false,
  });

  const items: WaitingItem[] = [];
  if (texts.data) {
    const waiting = texts.data.items.filter((t) => t.waitingSince !== null);
    items.push({
      key: 'texts',
      label: 'Unanswered texts',
      count: waiting.length,
      oldest: minIso(waiting.map((t) => t.waitingSince)),
      href: '/communications',
      icon: <MessageSquare size={14} aria-hidden />,
    });
  }
  if (missed.data) {
    // Callers already reached, called back or booked are on the list but not waiting.
    const waiting = missed.data.items.filter((m) => m.resolved === null);
    items.push({
      key: 'missed',
      label: 'Missed calls, no call back',
      count: waiting.length,
      oldest: minIso(waiting.flatMap((m) => m.attempts.map((a) => a.at))),
      href: '/phones',
      icon: <PhoneMissed size={14} aria-hidden />,
    });
  }
  if (forms.data) {
    items.push({
      key: 'forms',
      label: 'Forms to finish',
      count: forms.data.counts.incomplete,
      oldest: null,
      href: '/forms',
      icon: <FileSignature size={14} aria-hidden />,
    });
  }
  if (notes.data) {
    items.push({
      key: 'notes',
      label: notesTileLabel(notes.data.me.providerId ? notesScope : 'all'),
      count: notes.data.counts.unsigned,
      oldest: null,
      href: '/notes',
      icon: <NotebookPen size={14} aria-hidden />,
    });
  }

  const pending = [
    canComms && texting ? texts : null,
    canComms ? missed : null,
    canPatients ? forms : null,
    canNotes ? notes : null,
  ].some((q) => q?.isPending);
  return { items, pending };
}

export function WaitingStrip() {
  const { locationIds } = useOfficeScope();
  const { items, pending } = useWaitingItems();
  if (locationIds.length === 0 || (items.length === 0 && !pending)) return null;
  const owed = items.reduce((n, i) => n + i.count, 0);
  const search = ((prev: Record<string, unknown>) => ({ office: prev['office'] })) as never;

  return (
    <section className="home-waiting" aria-label="Waiting on us">
      <div className="home-waiting__head">
        <span className="home-waiting__title">
          <span className="home-waiting__dot" data-on={owed > 0 ? '' : undefined} aria-hidden />
          Waiting on us
        </span>
        <span className="home-waiting__sub">
          {owed === 0
            ? 'Nobody is waiting on a reply.'
            : `${owed} ${owed === 1 ? 'item needs' : 'items need'} a reply or a finish. Handled items drop off on their own.`}
        </span>
      </div>
      <ul className="home-waiting__items">
        {items.map((i) => (
          <li key={i.key}>
            <Link
              to={i.href as never}
              search={search}
              className="home-waiting__item"
              data-zero={i.count === 0 ? '' : undefined}
            >
              <span className="home-waiting__label">
                {i.icon}
                {i.label}
              </span>
              <span className="home-waiting__value">
                <span className="home-waiting__count vel-num">{i.count}</span>
                {i.count > 0 && i.oldest ? (
                  <span className="home-waiting__age">oldest {ageLabel(i.oldest)}</span>
                ) : null}
              </span>
            </Link>
          </li>
        ))}
        {pending && items.length === 0 ? (
          <li className="home-waiting__item">
            <Skeleton width={160} height={36} />
          </li>
        ) : null}
      </ul>
    </section>
  );
}
